import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { getCmsRestaurantsEndpointIdentifier } from './use-get-cms-restaurants-api';

const endpoint = 'ums/v1/restaurants';

type TData = void;
type TError = AxiosError;
type TVariables = { restaurantId: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUnassignAccountManagerApi(options?: Options): TReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ restaurantId }: TVariables): Promise<TData> => {
    try {
      await http.delete(`${endpoint}/${restaurantId}/account-manager`);
    } catch (error: any) {
      toast({
        description: error.response?.data?.message || 'Failed to remove the account manager',
        status: 'error',
      });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/account-manager/remove`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getCmsRestaurantsEndpointIdentifier]);
      toast({ description: 'Account manager removed', status: 'success' });
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
